<!-- PracticeCreationForm.vue -->
<template>
	<div class="practice-creation-form">
		<div class="practice-creation-form__container">
			<div v-if="isOffline" class="form-offline-banner" role="status">
				Нет интернета. Сохранение будет поставлено в очередь ({{ pendingQueueCount }}).
			</div>
			<img
				class="practice-creation-form__background"
				alt=""
				src="/assets/animaapp/rectangle.svg"
			/>

			<!-- Настройки практического задания -->
			<div class="practice-settings">
				<div class="practice-settings__header">
					<div class="practice-settings__header-background" />
					<div class="practice-settings__header-title">
						{{
							isEditMode
								? 'Редактирование практического задания'
								: 'Создание практического задания'
						}}
					</div>
					<button
						class="practice-settings__close-button"
						type="button"
						@click="handleClose"
						aria-label="Закрыть"
					>
						<img
							class="practice-settings__close-icon"
							alt="Закрыть"
							src="@/assets/cdn-animaapp/vector-8.svg"
						/>
					</button>
				</div>

				<!-- Название задания -->
				<div class="practice-settings__field">
					<label class="practice-settings__label">
						Название задания
						<span class="practice-required-mark" aria-hidden="true">*</span>
					</label>
					<input
						v-model="practiceTitle"
						type="text"
						class="practice-settings__input"
						:class="{ 'practice-settings__input--error': validationErrors.title }"
						placeholder="Введите название задания"
					/>
					<p v-if="validationErrors.title" class="practice-settings__error">
						{{ validationErrors.title }}
					</p>
				</div>

				<!-- Индикатор автосохранения -->
				<div v-if="lastSaveTimeFormatted" class="practice-settings__autosave">
					<span class="practice-settings__autosave-text">
						Последнее сохранение: {{ lastSaveTimeFormatted }}
					</span>
				</div>

				<!-- Галочка мини-теста -->
				<div class="practice-settings__type-section">
					<h3 class="practice-settings__section-title">Дополнительные опции</h3>
					<div class="practice-settings__type-buttons">
						<button
							type="button"
							class="practice-type-btn"
							:class="{ 'practice-type-btn--active': includeMiniTest }"
							@click="includeMiniTest = !includeMiniTest"
						>
							<div class="practice-type-btn__heading">
								<div class="practice-type-btn__icon">
									<svg
										width="24"
										height="24"
										viewBox="0 0 24 24"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M9 11L12 14L22 4"
											stroke="#0095EC"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
										<path
											d="M21 12V19C21 19.5304 20.7893 20.0391 20.4142 20.4142C20.0391 20.7893 19.5304 21 19 21H5C4.46957 21 3.96086 20.7893 3.58579 20.4142C3.21071 20.0391 3 19.5304 3 19V5C3 4.46957 3.21071 3.96086 3.58579 3.58579C3.96086 3.21071 4.46957 3 5 3H16"
											stroke="#0095EC"
											stroke-width="2"
											stroke-linecap="round"
											stroke-linejoin="round"
										/>
									</svg>
								</div>
								<div class="practice-type-btn__title">Мини-тест</div>
							</div>
							<div class="practice-type-btn__description">
								Добавить проверочный тест к практическому заданию
							</div>
						</button>
					</div>
				</div>
			</div>

			<!-- Содержание задания -->
			<div class="practice-content">
				<h3 class="practice-content__title">Содержание задания</h3>

				<div class="practice-section practice-section--content">
					<div class="practice-section__card">
						<TopicTextEditor
							v-model="practiceContent"
							editor-class="practice-content__textarea"
							placeholder="Опишите содержание практического задания..."
							:min-height="160"
						/>
					</div>
				</div>

				<!-- Вопросы для изучения -->
				<div class="practice-section practice-section--questions">
					<h4 class="practice-section__title">Вопросы для изучения</h4>
					<div class="practice-section__card">
						<div class="practice-questions">
							<div
								v-for="(question, index) in questions"
								:key="'question-' + index"
								class="practice-question"
							>
								<span class="practice-question__number">{{ index + 1 }}.</span>
								<input
									v-model="questions[index]"
									type="text"
									class="practice-question__input"
									:placeholder="`Введите вопрос ${index + 1}`"
								/>
								<button
									v-if="questions.length > 1"
									type="button"
									class="practice-question__remove"
									@click.stop="removeQuestion(index)"
									title="Удалить вопрос"
								>
									✕
								</button>
							</div>
						</div>
						<button
							type="button"
							class="practice-content__add-btn"
							@click="addQuestion"
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8 3V13M3 8H13"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
								/>
							</svg>
							Добавить вопрос
						</button>
					</div>
				</div>

				<!-- Дополнительные материалы -->
				<div class="practice-section practice-section--materials">
					<h4 class="practice-section__title">Дополнительные материалы</h4>
					<div class="practice-section__card">
						<div class="practice-content__field">
							<div class="practice-materials-upload">
								<input
									ref="materialsFileInput"
									type="file"
									multiple
									@change="handleMaterialsUpload"
									class="practice-content__file-input"
									id="practiceFileInput"
								/>
								<label
									for="practiceFileInput"
									class="practice-materials-upload__label"
								>
									<span class="practice-materials-upload__icon">
										<svg
											width="20"
											height="20"
											viewBox="0 0 20 20"
											fill="none"
											xmlns="http://www.w3.org/2000/svg"
										>
											<path
												d="M10.5684 0.250174C10.4227 0.0907898 10.2167 0 10.0007 0C9.78471 0 9.57876 0.0907898 9.43301 0.250174L5.33043 4.73735C5.04376 5.05089 5.06555 5.53746 5.37909 5.82412C5.69263 6.11079 6.17919 6.089 6.46586 5.77547L9.23148 2.75056V14.1026C9.23148 14.5274 9.57589 14.8718 10.0007 14.8718C10.4255 14.8718 10.7699 14.5274 10.7699 14.1026V2.75056L13.5356 5.77547C13.8222 6.089 14.3088 6.11079 14.6223 5.82412C14.9359 5.53746 14.9576 5.05089 14.671 4.73735L10.5684 0.250174Z"
												fill="white"
											/>
											<path
												d="M1.53846 13.0768C1.53846 12.652 1.19407 12.3076 0.769231 12.3076C0.344401 12.3076 5.37388e-07 12.652 5.37388e-07 13.0768V13.1332C-1.99754e-05 14.5358 -4.05617e-05 15.6664 0.119508 16.5556C0.243631 17.4788 0.50916 18.256 1.12651 18.8734C1.74387 19.4908 2.52117 19.7563 3.44436 19.8804C4.33356 19.9999 5.46414 19.9999 6.8668 19.9999H13.1332C14.5359 19.9999 15.6665 19.9999 16.5557 19.8804C17.4789 19.7563 18.2561 19.4908 18.8735 18.8734C19.4909 18.256 19.7564 17.4788 19.8805 16.5556C20 15.6664 20 14.5358 20 13.1332V13.0768C20 12.652 19.6556 12.3076 19.2308 12.3076C18.806 12.3076 18.4615 12.652 18.4615 13.0768C18.4615 14.5491 18.4599 15.5758 18.3558 16.3506C18.2546 17.1032 18.0695 17.5017 17.7856 17.7856C17.5017 18.0695 17.1033 18.2545 16.3507 18.3557C15.5759 18.4598 14.5491 18.4615 13.0769 18.4615H6.92308C5.45086 18.4615 4.42407 18.4598 3.64935 18.3557C2.89677 18.2545 2.49823 18.0695 2.21437 17.7856C1.93051 17.5017 1.74544 17.1032 1.64426 16.3506C1.54009 15.5758 1.53846 14.5491 1.53846 13.0768Z"
												fill="white"
											/>
										</svg>
									</span>
									<span class="practice-materials-upload__text">
										Загрузить файлы
									</span>
								</label>
							</div>
							<div class="practice-materials-upload__hint">
								<span class="practice-materials-upload__description">
									Добавьте инструкции, шаблоны или примеры
								</span>
								<br />
								<span class="practice-materials-upload__size-hint">
									Максимальный размер каждого файла:
									{{ MATERIAL_MAX_SIZE_MB }} МБ
								</span>
							</div>
							<p v-if="materialsError" class="practice-content__error">
								{{ materialsError }}
							</p>
						</div>

						<!-- Список загруженных материалов -->
						<div v-if="materials.length > 0" class="practice-materials-list">
							<div
								v-for="(file, index) in materials"
								:key="'material-' + index"
								class="practice-material-item"
							>
								<div class="practice-material-item__info">
									<span class="practice-material-item__name">
										{{ getFileName(file) }}
									</span>
									<span class="practice-material-item__size">
										{{ formatFileSize(getFileSize(file)) }}
									</span>
									<span
										v-if="isNewFile(file)"
										class="practice-material-item__badge"
									>
										Новый
									</span>
								</div>
								<button
									type="button"
									class="practice-material-item__remove"
									@click="removeMaterial(index)"
									title="Удалить файл"
								>
									✕
								</button>
							</div>
						</div>
					</div>
				</div>

				<!-- Конструктор мини-теста -->
				<div
					v-if="includeMiniTest"
					class="practice-section practice-section--mini-test"
				>
					<div class="practice-section__header">
						<h4 class="practice-section__title">Конструктор мини-теста</h4>
						<span class="practice-section__limit">
							{{ miniTestQuestions.length }}/10 вопросов
						</span>
					</div>
					<div class="practice-section__card practice-section__card--mini-test">
						<!-- Кнопка добавления вопроса -->
						<button
							v-if="miniTestQuestions.length < 10"
							type="button"
							class="practice-mini-test__add-button"
							@click="handleAddMiniTestQuestion"
						>
							<svg
								width="16"
								height="16"
								viewBox="0 0 16 16"
								fill="none"
								xmlns="http://www.w3.org/2000/svg"
							>
								<path
									d="M8 3V13M3 8H13"
									stroke="currentColor"
									stroke-width="2"
									stroke-linecap="round"
								/>
							</svg>
							Добавить вопрос
						</button>

						<!-- Пустое состояние -->
						<div
							v-if="miniTestQuestions.length === 0"
							class="practice-mini-test__empty"
						>
							<p class="practice-mini-test__empty-text">
								Вопросы не добавлены. Нажмите "Добавить вопрос" для создания.
							</p>
						</div>

						<!-- Список вопросов -->
						<div v-else class="practice-mini-test__list">
							<div
								v-for="(question, index) in miniTestQuestions"
								:key="'minitest-' + question.id"
								class="mini-test-question"
							>
								<div class="mini-test-question__header">
									<div class="mini-test-question__number">
										Вопрос {{ index + 1 }}
									</div>
									<div class="mini-test-question__actions">
										<button
											type="button"
											class="mini-test-question__toggle"
											@click="toggleMiniTestQuestion(question.id)"
											:title="
												question.isExpanded
													? 'Свернуть вопрос'
													: 'Развернуть вопрос'
											"
										>
											{{ question.isExpanded ? '−' : '+' }}
										</button>
										<button
											type="button"
											class="mini-test-question__remove"
											@click="handleRemoveMiniTestQuestion(index)"
											title="Удалить вопрос"
										>
											✕
										</button>
									</div>
								</div>

								<div
									v-show="question.isExpanded"
									class="mini-test-question__content"
								>
									<!-- Текст вопроса -->
									<div class="mini-test-question__field">
										<label class="mini-test-question__label"
											>Текст вопроса</label
										>
										<textarea
											v-model="question.text"
											class="mini-test-question__textarea"
											placeholder="Введите текст вопроса"
											rows="2"
										></textarea>
									</div>

									<!-- Тип вопроса -->
									<div class="mini-test-question__field">
										<label class="mini-test-question__label">Тип вопроса</label>
										<select
											v-model="question.type"
											class="mini-test-question__select"
											@change="handleMiniTestQuestionTypeChange(question)"
										>
											<option value="single">Один правильный ответ</option>
											<option value="multiple">
												Несколько правильных ответов
											</option>
											<option value="text">Текстовый ответ</option>
										</select>
									</div>

									<!-- Варианты ответов -->
									<div
										v-if="
											question.type === 'single' || question.type === 'multiple'
										"
										class="mini-test-question__answers"
									>
										<div class="mini-test-question__answers-header">
											<label class="mini-test-question__label"
												>Варианты ответов</label
											>
											<button
												type="button"
												class="mini-test-question__add-answer"
												@click="addMiniTestAnswer(question)"
											>
												+ Добавить вариант
											</button>
										</div>

										<div
											v-for="(answer, answerIndex) in question.answers"
											:key="'answer-' + answer.id"
											class="mini-test-answer"
										>
											<input
												v-if="question.type === 'single'"
												type="radio"
												:name="`mini-test-question-${question.id}`"
												:checked="answer.isCorrect"
												@change="
													setMiniTestCorrectAnswer(question, answerIndex)
												"
												class="mini-test-answer__radio"
											/>
											<input
												v-else
												type="checkbox"
												v-model="answer.isCorrect"
												class="mini-test-answer__checkbox"
											/>
											<input
												v-model="answer.text"
												type="text"
												class="mini-test-answer__input"
												:placeholder="`Вариант ${answerIndex + 1}`"
											/>
											<button
												type="button"
												class="mini-test-answer__remove"
												@click="removeMiniTestAnswer(question, answerIndex)"
												:disabled="question.answers.length <= 2"
												title="Удалить вариант"
											>
												✕
											</button>
										</div>
									</div>

									<!-- Правильный ответ для текстового типа -->
									<div
										v-if="question.type === 'text'"
										class="mini-test-question__field"
									>
										<label class="mini-test-question__label">
											Правильный ответ (для проверки)
										</label>
										<input
											v-model="question.correctText"
											type="text"
											class="mini-test-question__input"
											placeholder="Введите правильный ответ"
										/>
									</div>

									<!-- Пояснение к ответу -->
									<div class="mini-test-question__field mini-test-question__field--explanation">
										<label class="mini-test-question__label">
											<span class="mini-test-question__label-icon">💡</span>
											Пояснение к ответу
											<span class="mini-test-question__label-hint">(необязательно)</span>
										</label>
										<textarea
											v-model="question.explanation"
											class="mini-test-question__textarea mini-test-question__textarea--explanation"
											placeholder="Объясните, почему этот ответ правильный. Студент увидит это после проверки."
											rows="3"
										></textarea>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>

				<!-- Кнопки действий -->
				<div class="practice-actions">
					<span
						class="practice-actions__preview-tooltip"
						:title="practicePreviewDisabledHint"
					>
						<button
							type="button"
							class="practice-actions__button practice-actions__button--ghost"
							:disabled="!canOpenPracticePreview"
							@click="openPracticePreviewInNewTab"
						>
							Предпросмотр
						</button>
					</span>
					<button
						v-if="false"
						type="button"
						class="practice-actions__button practice-actions__button--secondary"
						@click="handleSaveDraft"
					>
						Сохранить как черновик
					</button>
					<div class="practice-actions__primary-group">
						<button
							type="button"
							class="practice-actions__button practice-actions__button--primary"
							:class="{
								'practice-actions__button--disabled':
									isSaveButtonDisabled || isSaving,
							}"
							:disabled="isSaveButtonDisabled || isSaving"
							@click="handleSavePractice"
						>
							{{ saveButtonText }}
						</button>
						<p
							v-if="getPracticeSubmitErrorMessage()"
							class="practice-actions__error"
						>
							{{ getPracticeSubmitErrorMessage() }}
						</p>
					</div>
				</div>
			</div>
		</div>

		<!-- Модальное окно предпросмотра -->
		<div
			v-if="showPreviewModal"
			class="preview-modal"
			@click.self="handleClosePreview"
		>
			<div class="preview-modal__content">
				<div class="preview-modal__header">
					<h3 class="preview-modal__title">Предпросмотр задания</h3>
					<button
						type="button"
						class="preview-modal__close"
						@click="handleClosePreview"
						title="Закрыть"
					>
						✕
					</button>
				</div>
				<div class="preview-modal__body">
					<div class="preview-section">
						<h4 class="preview-section__title">Название</h4>
						<p class="preview-section__text">
							{{ practiceTitle || 'Без названия' }}
						</p>
					</div>

					<div
						v-if="hasMeaningfulText(practiceContent)"
						class="preview-section"
					>
						<h4 class="preview-section__title">Содержание задания</h4>
						<div
							class="preview-section__text"
							v-html="sanitizePreviewHtml(practiceContent)"
						></div>
					</div>

					<div v-if="filteredQuestions.length > 0" class="preview-section">
						<h4 class="preview-section__title">Вопросы для изучения</h4>
						<ul class="preview-section__list">
							<li
								v-for="(question, index) in filteredQuestions"
								:key="'preview-question-' + index"
							>
								{{ question }}
							</li>
						</ul>
					</div>

					<div v-if="materials.length > 0" class="preview-section">
						<h4 class="preview-section__title">Дополнительные материалы</h4>
						<ul class="preview-materials">
							<li
								v-for="(file, index) in materials"
								:key="'preview-material-' + index"
								class="preview-materials__item"
							>
								<span class="preview-materials__name">
									{{ getFileName(file) }}
								</span>
								<span class="preview-materials__size">
									{{ formatFileSize(getFileSize(file)) }}
								</span>
							</li>
						</ul>
					</div>

					<div
						v-if="includeMiniTest && miniTestQuestions.length > 0"
						class="preview-section"
					>
						<h4 class="preview-section__title">
							Мини-тест ({{ miniTestQuestions.length }} вопросов)
						</h4>
						<div class="preview-test">
							<div
								v-for="(question, qIndex) in miniTestQuestions"
								:key="'preview-minitest-' + question.id"
								class="preview-test-question"
							>
								<div class="preview-test-question__number">
									Вопрос {{ qIndex + 1 }}
								</div>
								<div class="preview-test-question__text">
									{{ question.text || 'Текст вопроса не указан' }}
								</div>

								<div
									v-if="
										question.type === 'single' || question.type === 'multiple'
									"
									class="preview-test-answers"
								>
									<div
										v-for="(answer, aIndex) in question.answers"
										:key="'preview-answer-' + answer.id"
										class="preview-test-answer"
										:class="{
											'preview-test-answer--correct': answer.isCorrect,
										}"
									>
										<input
											:type="question.type === 'single' ? 'radio' : 'checkbox'"
											:name="`preview-q-${question.id}`"
											:checked="answer.isCorrect"
											disabled
											class="preview-test-answer__input"
										/>
										<span class="preview-test-answer__text">
											{{ answer.text || `Вариант ${aIndex + 1}` }}
										</span>
										<span
											v-if="answer.isCorrect"
											class="preview-test-answer__badge"
										>
											✓ Правильный
										</span>
									</div>
								</div>

								<div
									v-else-if="question.type === 'text'"
									class="preview-test-text-answer"
								>
									<input
										type="text"
										class="preview-test-text-answer__input"
										placeholder="Введите ваш ответ"
										disabled
									/>
								</div>
							</div>
						</div>
					</div>
				</div>

				<div class="preview-modal__footer">
					<button
						type="button"
						class="preview-modal__button"
						@click="handleClosePreview"
					>
						Закрыть
					</button>
				</div>
			</div>
		</div>
	</div>
</template>

<script>
import DOMPurify from 'dompurify'
import TopicTextEditor from '../../../../components/ProjectCreateCourse/common/TopicTextEditor.vue'
import { openInternalRouteInContext } from '@/utils/bitrixInternalNavigation'

const MATERIAL_MAX_SIZE_MB = 50
const MATERIAL_MAX_SIZE_BYTES = MATERIAL_MAX_SIZE_MB * 1024 * 1024

export default {
	name: 'PracticeCreationForm',
	components: {
		TopicTextEditor,
	},
	props: {
		currentModuleId: {
			type: [String, Number],
			default: null,
		},
		availableModules: {
			type: Array,
			default: () => [],
		},
		editingPractice: {
			type: Object,
			default: null,
		},
	},
	emits: ['save-practice', 'save-draft', 'cancel'],
	data() {
		return {
			// Редактирование
			editingPracticeId: null,

			practiceTitle: '',
			selectedModule: '',
			includeMiniTest: false,
			practiceContent: '',
			questions: [''],
			materials: [],
			materialsError: '',
			submitError: '',
			validationErrors: {
				title: '',
			},
			deletedMaterials: [],

			// Мини-тест
			miniTestQuestions: [],
			nextMiniTestQuestionId: 1,
			nextMiniTestAnswerId: 1,

			// Автосохранение
			lastSaveTime: null,

			// Предпросмотр
			showPreviewModal: false,

			// Флаг сохранения (для предотвращения ложных предупреждений)
			isSaved: false,

			isLoadingData: false, // Флаг загрузки данных (блокирует watchers)
			originalData: null, // Снимок исходного состояния

			// Константы
			MATERIAL_MAX_SIZE_MB,
			isOffline: typeof navigator !== 'undefined' ? !navigator.onLine : false,
			pendingQueueCount: 0,
			submitQueueKey: 'practice_form_submit_queue_v1',
			isSaving: false,
		}
	},
	computed: {
		isEditMode() {
			return this.editingPracticeId !== null
		},
		lastSaveTimeFormatted() {
			if (!this.lastSaveTime) return null
			const date = new Date(this.lastSaveTime)
			return date.toLocaleTimeString('ru-RU', {
				hour: '2-digit',
				minute: '2-digit',
			})
		},
		filteredQuestions() {
			return this.questions.filter(q => q && q.trim())
		},
		// 🔥 Динамический текст кнопки (добавить в computed)
		saveButtonText() {
			if (this.isEditMode) {
				const hasChanges = this.hasUnsavedChanges()
				return hasChanges ? 'Применить изменения' : 'Сохранено'
			} else {
				return 'Создать задание' // Заменить на нужный текст для каждой формы
			}
		},

		// 🔥 Отключение кнопки если нет изменений
		isSaveButtonDisabled() {
			if (this.isEditMode) {
				return !this.hasUnsavedChanges()
			}
			return false
		},
		canOpenPracticePreview() {
			const moduleId = this.currentModuleId || this.selectedModule
			return (
				moduleId !== null &&
				moduleId !== undefined &&
				moduleId !== '' &&
				this.editingPracticeId !== null &&
				this.editingPracticeId !== undefined &&
				this.editingPracticeId !== '' &&
				!String(moduleId).startsWith('temp_') &&
				!String(this.editingPracticeId).startsWith('temp_')
			)
		},
		practicePreviewDisabledHint() {
			return this.canOpenPracticePreview
				? ''
				: 'Сначала создайте практику, чтобы открыть предпросмотр.'
		},
	},
	mounted() {
		// Устанавливаем модуль
		if (this.currentModuleId) {
			this.selectedModule = this.currentModuleId
		}

		// Если есть данные для редактирования - загружаем их
		if (this.editingPractice) {
			this.loadEditingData(this.editingPractice)
		} else {
			// Иначе пробуем восстановить из localStorage
			this.restoreFormData()
		}
		this.pendingQueueCount = this.getSubmitQueue().length
		window.addEventListener('online', this.handleOnline)
		window.addEventListener('offline', this.handleOffline)
		if (!this.isOffline) {
			this.flushSubmitQueue()
		}
	},
	beforeUnmount() {
		window.removeEventListener('online', this.handleOnline)
		window.removeEventListener('offline', this.handleOffline)
	},
	watch: {
		editingPractice: {
			handler(newPractice) {
				if (newPractice) {
					this.loadEditingData(newPractice)
				} else {
					this.resetForm()
				}
			},
			immediate: false,
			deep: true,
		},
		currentModuleId: {
			handler(newId) {
				if (newId) {
					this.selectedModule = newId
				}
			},
		},
		// 🔥 ОБНОВЛЕННЫЕ watchers с проверкой isLoadingData
		practiceTitle() {
			if (!this.isLoadingData) {
				if (this.validationErrors.title && this.practiceTitle.trim()) {
					this.validationErrors.title = ''
				}
				this.debouncedSaveFormData()
				this.isSaved = false
			}
		},
		someField() {
			if (!this.isLoadingData) {
				this.saveFormData() // если есть автосохранение
				this.isSaved = false
			}
		},
		practiceContent() {
			if (!this.isLoadingData) {
				this.debouncedSaveFormData()
				this.isSaved = false
			}
		},
		questions: {
			handler() {
				if (!this.isLoadingData) {
					this.debouncedSaveFormData()
					this.isSaved = false
				}
			},
			deep: true,
		},
		includeMiniTest() {
			if (!this.isLoadingData) {
				this.debouncedSaveFormData()
				this.isSaved = false
			}
		},
		miniTestQuestions: {
			handler() {
				if (!this.isLoadingData) {
					this.debouncedSaveFormData()
					this.isSaved = false
				}
			},
			deep: true,
		},
		materials: {
			handler() {
				if (!this.isLoadingData) {
					this.isSaved = false
				}
			},
			deep: true,
		},
	},
	created() {
		// Создаем debounced версию saveFormData
		this.debouncedSaveFormData = this.debounce(() => {
			this.saveFormData()
		}, 1000)
	},
	methods: {
		getPracticeSubmitErrorMessage() {
			return this.submitError || ''
		},
		handleOnline() {
			this.isOffline = false
			this.flushSubmitQueue()
		},
		handleOffline() {
			this.isOffline = true
		},
		getSubmitQueue() {
			try {
				const raw = localStorage.getItem(this.submitQueueKey)
				const parsed = raw ? JSON.parse(raw) : []
				return Array.isArray(parsed) ? parsed : []
			} catch (error) {
				console.error('Ошибка чтения очереди отправки практики:', error)
				return []
			}
		},
		setSubmitQueue(queue) {
			try {
				localStorage.setItem(this.submitQueueKey, JSON.stringify(queue))
			} catch (error) {
				console.error('Ошибка записи очереди отправки практики:', error)
			}
			this.pendingQueueCount = Array.isArray(queue) ? queue.length : 0
		},
		enqueueSubmit(eventName, payload) {
			const queue = this.getSubmitQueue()
			queue.push({ eventName, payload, createdAt: Date.now() })
			this.setSubmitQueue(queue)
		},
		flushSubmitQueue() {
			if (typeof navigator !== 'undefined' && !navigator.onLine) return
			const queue = this.getSubmitQueue()
			if (!queue.length) return
			queue.forEach(item => {
				if (item && item.eventName && item.payload) {
					this.$emit(item.eventName, item.payload)
				}
			})
			this.setSubmitQueue([])
		},
		// ============================================
		// 🔥 НОВЫЕ МЕТОДЫ для отслеживания изменений
		// ============================================

		/**
		 * Захватывает текущее состояние формы как исходное
		 */
		captureOriginalData() {
			this.originalData = {
				practiceTitle: this.practiceTitle,
				practiceContent: this.practiceContent,
				questions: JSON.stringify(this.questions),
				includeMiniTest: this.includeMiniTest,
				miniTestQuestions: JSON.stringify(
					this.miniTestQuestions.map(q => ({
						text: q.text,
						type: q.type,
						answers: q.answers
							? q.answers.map(a => ({
									text: a.text,
									isCorrect: a.isCorrect,
							  }))
							: [],
						correctText: q.correctText || '',
						explanation: q.explanation || '',
					}))
				),
				// Для файлов сохраняем только имена для сравнения
				materialFileNames: this.materials.map(m => this.getFileName(m)),
			}
			console.log('📸 Снимок исходных данных практики:', this.originalData)
		},

		/**
		 * Проверяет, есть ли реальные изменения относительно исходных данных
		 */
		hasUnsavedChanges() {
			// Если форма была успешно сохранена — изменений нет
			if (this.isSaved) {
				return false
			}
			if (!this.originalData) {
				// Режим создания - проверяем заполнено ли что-то
				return /* проверка полей */
			}
			// Если нет снимка исходных данных — значит это создание нового задания
			// Проверяем, заполнено ли хоть что-то
			if (!this.originalData) {
				return (
					this.practiceTitle ||
					this.practiceContent ||
					this.questions.some(q => q && q.trim()) ||
					this.materials.length > 0 ||
					this.miniTestQuestions.length > 0
				)
			}

			// Сравниваем текущее состояние с исходным
			const currentMiniTestQuestions = JSON.stringify(
				this.miniTestQuestions.map(q => ({
					text: q.text,
					type: q.type,
					answers: q.answers
						? q.answers.map(a => ({
								text: a.text,
								isCorrect: a.isCorrect,
						  }))
						: [],
					correctText: q.correctText || '',
					explanation: q.explanation || '',
				}))
			)

			const current = {
				practiceTitle: this.practiceTitle,
				practiceContent: this.practiceContent,
				questions: JSON.stringify(this.questions),
				includeMiniTest: this.includeMiniTest,
				miniTestQuestions: currentMiniTestQuestions,
				materialFileNames: this.materials.map(m => this.getFileName(m)),
			}

			// Проверяем каждое поле
			const changes = {
				practiceTitle:
					current.practiceTitle !== this.originalData.practiceTitle,
				practiceContent: current.practiceContent !== this.originalData.practiceContent,
				questions: current.questions !== this.originalData.questions,
				includeMiniTest:
					current.includeMiniTest !== this.originalData.includeMiniTest,
				miniTestQuestions:
					current.miniTestQuestions !== this.originalData.miniTestQuestions,
				materials:
					JSON.stringify(current.materialFileNames) !==
					JSON.stringify(this.originalData.materialFileNames),
			}

			const hasChanges = Object.values(changes).some(changed => changed)

			if (hasChanges) {
				console.log('🔍 Обнаружены изменения в практике:', changes)
			}

			return hasChanges
		},
		// ============================================
		// УТИЛИТЫ
		// ============================================

		debounce(func, wait) {
			let timeout
			return function executedFunction(...args) {
				const later = () => {
					clearTimeout(timeout)
					func(...args)
				}
				clearTimeout(timeout)
				timeout = setTimeout(later, wait)
			}
		},

		// ============================================
		// МЕТОДЫ СБРОСА И ЗАГРУЗКИ
		// ============================================

		resetForm() {
			this.editingPracticeId = null
			this.practiceTitle = ''
			this.selectedModule = this.currentModuleId || ''
			this.includeMiniTest = false
			this.practiceContent = ''
			this.questions = ['']
			this.materials = []
			this.materialsError = ''
			this.submitError = ''
			this.validationErrors = { title: '' }
			this.deletedMaterials = []
			this.miniTestQuestions = []
			this.nextMiniTestQuestionId = 1
			this.nextMiniTestAnswerId = 1
			this.showPreviewModal = false
			this.lastSaveTime = null
			this.isSaved = false
			this.originalData = null
			this.clearFormData()
		},

		loadEditingData(data) {
			if (!data) return

			// 🔥 Блокируем watchers на время загрузки
			this.isLoadingData = true

			console.log('📥 Загрузка данных для редактирования практики:', data)

			this.editingPracticeId = data.id || null
			this.practiceTitle = data.title || ''
			this.selectedModule =
				data.module || data.module_id || this.currentModuleId || ''
			this.practiceContent =
				data.content || data.task_description || data.goal || data.description || ''

			// Вопросы - парсим JSON если это строка (из БД)
			let parsedQuestions = data.questions
			if (typeof parsedQuestions === 'string') {
				try {
					parsedQuestions = JSON.parse(parsedQuestions)
				} catch (e) {
					console.warn('⚠️ Ошибка парсинга questions:', e)
					parsedQuestions = []
				}
			}
			if (Array.isArray(parsedQuestions) && parsedQuestions.length > 0) {
				this.questions = [...parsedQuestions]
			} else {
				this.questions = ['']
			}

			// Материалы - парсим JSON если это строка (из БД)
			let parsedMaterials = data.materials
			if (typeof parsedMaterials === 'string') {
				try {
					parsedMaterials = JSON.parse(parsedMaterials)
				} catch (e) {
					console.warn('⚠️ Ошибка парсинга materials:', e)
					parsedMaterials = []
				}
			}
			if (Array.isArray(parsedMaterials) && parsedMaterials.length > 0) {
				this.materials = [...parsedMaterials]
				console.log('📎 Материалы загружены:', this.materials)
			} else {
				this.materials = []
			}

			this.deletedMaterials = []
			this.materialsError = ''
			this.submitError = ''
			this.validationErrors = { title: '' }

			// Мини-тест - парсим JSON если это строка (из БД)
			let miniTestData = data.miniTest || data.mini_test
			console.log('🔍 miniTestData до парсинга:', miniTestData)

			if (typeof miniTestData === 'string') {
				try {
					miniTestData = JSON.parse(miniTestData)
				} catch (e) {
					console.warn('⚠️ Ошибка парсинга mini_test:', e)
					miniTestData = null
				}
			}

			console.log('🔍 miniTestData после парсинга:', miniTestData)

			if (
				miniTestData &&
				Array.isArray(miniTestData.questions) &&
				miniTestData.questions.length > 0
			) {
				this.includeMiniTest = true
				console.log('🔍 Вопросы мини-теста из данных:', miniTestData.questions)

				this.miniTestQuestions = miniTestData.questions.map((q, index) => {
					console.log(`🔍 Вопрос ${index + 1}:`, q)
					console.log(`🔍 Ответы вопроса ${index + 1}:`, q.answers)

					return {
						id: index + 1,
						text: q.text || '',
						type: q.type || 'single',
						answers: Array.isArray(q.answers)
							? q.answers.map((a, aIndex) => {
									console.log(
										`🔍 Ответ ${aIndex + 1}:`,
										a,
										'isCorrect:',
										a.isCorrect,
										'is_correct:',
										a.is_correct
									)
									return {
										id: aIndex + 1,
										text: a.text || '',
										isCorrect: a.isCorrect || a.is_correct || false,
									}
							  })
							: [],
						correctText: q.correctText || q.correct_text || '',
						explanation: q.explanation || '', // Пояснение к ответу
						isExpanded: false,
					}
				})
				this.nextMiniTestQuestionId = this.miniTestQuestions.length + 1

				// Вычисляем следующий ID для ответов
				let maxAnswerId = 0
				this.miniTestQuestions.forEach(q => {
					q.answers.forEach(a => {
						if (a.id > maxAnswerId) maxAnswerId = a.id
					})
				})
				this.nextMiniTestAnswerId = maxAnswerId + 1

				console.log(
					'✅ miniTestQuestions после загрузки:',
					JSON.stringify(this.miniTestQuestions, null, 2)
				)
			} else {
				this.includeMiniTest = data.includeMiniTest || false
				this.miniTestQuestions = []
				console.log(
					'⚠️ miniTest не найден или пустой, includeMiniTest:',
					this.includeMiniTest
				)
			}

			console.log('✅ Данные практики загружены:', {
				title: this.practiceTitle,
				content: this.practiceContent,
				questions: this.questions,
				materials: this.materials.length,
				miniTest: this.miniTestQuestions.length,
			})

			// 🔥 Сохраняем снимок исходного состояния
			this.captureOriginalData()

			// 🔥 Разблокируем watchers
			this.isLoadingData = false
			this.isSaved = true // Данные загружены, изменений пока нет
		},

		// ============================================
		// МЕТОДЫ ДЛЯ РАБОТЫ С LOCALSTORAGE
		// ============================================

		getFormDataKey() {
			return `practiceCreationForm_${
				this.currentModuleId || 'default'
			}_formData`
		},

		saveFormData() {
			// Не сохраняем в localStorage если редактируем существующее
			if (this.editingPracticeId) return

			const formData = {
				practiceTitle: this.practiceTitle,
				practiceContent: this.practiceContent,
				questions: this.questions,
				includeMiniTest: this.includeMiniTest,
				miniTestQuestions: this.miniTestQuestions,
				timestamp: Date.now(),
			}

			try {
				localStorage.setItem(this.getFormDataKey(), JSON.stringify(formData))
				this.lastSaveTime = Date.now()
			} catch (error) {
				console.error('❌ Ошибка сохранения данных формы:', error)
			}
		},

		restoreFormData() {
			// Не восстанавливаем если редактируем
			if (this.editingPracticeId) return

			try {
				const savedData = localStorage.getItem(this.getFormDataKey())
				if (savedData) {
					const formData = JSON.parse(savedData)
					const isExpired =
						Date.now() - formData.timestamp > 24 * 60 * 60 * 1000

					if (!isExpired) {
						console.log('🔄 Восстановление данных формы из localStorage')
						this.practiceTitle = formData.practiceTitle || ''
						this.practiceContent = formData.practiceContent || ''
						this.questions = formData.questions || ['']
						this.includeMiniTest = formData.includeMiniTest || false
						this.miniTestQuestions = formData.miniTestQuestions || []
						this.lastSaveTime = formData.timestamp
					} else {
						this.clearFormData()
					}
				}
			} catch (error) {
				console.error('❌ Ошибка восстановления данных формы:', error)
				this.clearFormData()
			}
		},

		clearFormData() {
			try {
				localStorage.removeItem(this.getFormDataKey())
			} catch (error) {
				console.error('❌ Ошибка очистки данных формы:', error)
			}
		},

		// ============================================
		// ВСПОМОГАТЕЛЬНЫЕ МЕТОДЫ ДЛЯ ФАЙЛОВ
		// ============================================

		isNewFile(file) {
			return file instanceof File
		},

		getFileName(file) {
			if (!file) return 'Неизвестный файл'
			return file.name || file.file_name || file.fileName || 'Без имени'
		},

		getFileSize(file) {
			if (!file) return 0
			return file.size || file.file_size || file.fileSize || 0
		},

		formatFileSize(bytes) {
			if (!bytes || bytes === 0) return '0 Bytes'
			const k = 1024
			const sizes = ['Bytes', 'KB', 'MB', 'GB']
			const i = Math.floor(Math.log(bytes) / Math.log(k))
			return `${Math.round((bytes / Math.pow(k, i)) * 100) / 100} ${sizes[i]}`
		},

		// ============================================
		// ВОПРОСЫ
		// ============================================

		addQuestion() {
			this.questions.push('')
		},

		removeQuestion(index) {
			if (this.questions.length <= 1) return
			this.questions.splice(index, 1)
		},

		// ============================================
		// МАТЕРИАЛЫ
		// ============================================

		handleMaterialsUpload(event) {
			const input = event.target
			const newFiles = Array.from(input.files || [])
			this.materialsError = ''

			console.log('📁 Загрузка файлов:', newFiles.length)

			if (!newFiles.length) {
				input.value = ''
				return
			}

			const validFiles = []
			const errors = []
			const maxFiles = 10

			// Проверка общего количества файлов
			if (this.materials.length + newFiles.length > maxFiles) {
				this.materialsError = `Можно загрузить не более ${maxFiles} файлов. Сейчас загружено: ${this.materials.length}.`
				input.value = ''
				return
			}

			for (const file of newFiles) {
				// Проверка размера
				if (file.size && file.size > MATERIAL_MAX_SIZE_BYTES) {
					errors.push(
						`"${file.name}" слишком большой (максимум: ${MATERIAL_MAX_SIZE_MB} МБ)`
					)
					continue
				}

				// Проверка на дубликаты
				const isDuplicate = this.materials.some(existingFile => {
					const existingName = this.getFileName(existingFile)
					return existingName === file.name
				})

				if (isDuplicate) {
					errors.push(`"${file.name}" уже загружен`)
					continue
				}

				validFiles.push(file)
			}

			if (errors.length > 0) {
				this.materialsError = `Некоторые файлы не были загружены: ${errors.join(
					', '
				)}.`
			}

			if (validFiles.length > 0) {
				// Используем Vue.set или spread для реактивности
				this.materials = [...this.materials, ...validFiles]
				console.log(
					'✅ Файлы добавлены:',
					validFiles.length,
					'Всего:',
					this.materials.length
				)
			}

			// Очищаем input для возможности повторной загрузки того же файла
			input.value = ''
		},

		removeMaterial(index) {
			if (!window.confirm('Удалить материал?')) {
				return
			}
			const material = this.materials[index]

			// Если это существующий файл из БД - добавляем в список удаленных
			if (material && !this.isNewFile(material)) {
				console.log(
					'🗑️ Добавление файла в список удаленных:',
					this.getFileName(material)
				)
				this.deletedMaterials.push(material)
			}

			this.materials.splice(index, 1)
			console.log('📁 Файл удален. Осталось:', this.materials.length)
		},

		// ============================================
		// МИНИ-ТЕСТ
		// ============================================

		handleAddMiniTestQuestion() {
			if (this.miniTestQuestions.length >= 10) {
				alert('Максимальное количество вопросов - 10')
				return
			}

			const newQuestion = {
				id: this.nextMiniTestQuestionId++,
				text: '',
				type: 'single',
				answers: [
					{ id: this.nextMiniTestAnswerId++, text: '', isCorrect: false },
					{ id: this.nextMiniTestAnswerId++, text: '', isCorrect: false },
				],
				correctText: '',
				explanation: '', // Пояснение к ответу
				isExpanded: true,
			}
			this.miniTestQuestions.push(newQuestion)
		},

		handleRemoveMiniTestQuestion(index) {
			if (confirm('Вы уверены, что хотите удалить этот вопрос?')) {
				this.miniTestQuestions.splice(index, 1)
			}
		},

		toggleMiniTestQuestion(questionId) {
			const question = this.miniTestQuestions.find(q => q.id === questionId)
			if (question) {
				question.isExpanded = !question.isExpanded
			}
		},

		handleMiniTestQuestionTypeChange(question) {
			if (question.type === 'text') {
				question.answers = []
				question.correctText = ''
			} else {
				if (!question.answers || question.answers.length === 0) {
					question.answers = [
						{ id: this.nextMiniTestAnswerId++, text: '', isCorrect: false },
						{ id: this.nextMiniTestAnswerId++, text: '', isCorrect: false },
					]
				}
				question.correctText = ''
			}
		},

		addMiniTestAnswer(question) {
			if (!question.answers) {
				question.answers = []
			}
			question.answers.push({
				id: this.nextMiniTestAnswerId++,
				text: '',
				isCorrect: false,
			})
		},

		removeMiniTestAnswer(question, answerIndex) {
			if (question.answers && question.answers.length > 2) {
				if (!window.confirm('Удалить вариант ответа?')) {
					return
				}
				question.answers.splice(answerIndex, 1)
			}
		},

		setMiniTestCorrectAnswer(question, answerIndex) {
			if (!question.answers) return
			question.answers.forEach((answer, index) => {
				answer.isCorrect = index === answerIndex
			})
		},

		// ============================================
		// ДЕЙСТВИЯ ФОРМЫ
		// ============================================

		validateForm() {
			this.submitError = ''
			this.materialsError = ''
			this.validationErrors = { title: '' }

			// Проверка названия
			if (!this.practiceTitle.trim()) {
				this.submitError = 'Введите название задания'
				this.validationErrors.title = 'Введите название задания'
				return false
			}
			// Проверка размера файлов
			for (const file of this.materials) {
				const size = this.getFileSize(file)
				if (size && size > MATERIAL_MAX_SIZE_BYTES) {
					const name = this.getFileName(file)
					this.materialsError = `Файл "${name}" слишком большой. Максимальный размер: ${MATERIAL_MAX_SIZE_MB} МБ.`
					return false
				}
			}

			// Проверка мини-теста: правильный ответ должен быть выбран
			if (this.includeMiniTest && this.miniTestQuestions.length > 0) {
				for (let i = 0; i < this.miniTestQuestions.length; i++) {
					const question = this.miniTestQuestions[i]
					if (question.type === 'single' || question.type === 'multiple') {
						const hasCorrectAnswer =
							question.answers && question.answers.some(a => a.isCorrect)
						if (!hasCorrectAnswer) {
							const confirmed = window.confirm(
								`⚠️ Внимание!\n\nВ вопросе ${
									i + 1
								} мини-теста не выбран правильный ответ.\n\nПродолжить сохранение без указания правильного ответа?`
							)
							if (!confirmed) {
								// Разворачиваем вопрос для удобства редактирования
								question.isExpanded = true
								return false
							}
						}
					} else if (
						question.type === 'text' &&
						!question.correctText?.trim()
					) {
						const confirmed = window.confirm(
							`⚠️ Внимание!\n\nВ вопросе ${
								i + 1
							} мини-теста не указан правильный ответ для текстового вопроса.\n\nПродолжить сохранение без указания правильного ответа?`
						)
						if (!confirmed) {
							question.isExpanded = true
							return false
						}
					}
				}
			}

			return true
		},

		handleSavePractice() {
			if (!this.validateForm()) return
			if (this.isSaving) return
			this.isSaving = true

			const filteredQuestions = [...this.questions]
				.filter(q => q && q.trim())
				.map(q => q.trim())

			// Формируем miniTest с разворачиванием Proxy
			const miniTestData =
				this.includeMiniTest && this.miniTestQuestions.length > 0
					? {
							questions: [...this.miniTestQuestions].map(q => ({
								text: q.text,
								type: q.type,
								answers: q.answers
									? [...q.answers].map(a => ({
											text: a.text,
											isCorrect: a.isCorrect,
									  }))
									: [],
								correctText: q.correctText || '',
								explanation: q.explanation || '', // Пояснение к ответу
							})),
					  }
					: null

			const practiceData = {
				...(this.editingPracticeId && { id: this.editingPracticeId }),
				title: this.practiceTitle.trim(),
				module: this.selectedModule || this.currentModuleId,
				content: this.normalizeRichText(this.practiceContent),
				questions: filteredQuestions,
				materials: [...this.materials],
				deletedMaterials: [...this.deletedMaterials],
				includeMiniTest: this.includeMiniTest,
				miniTest: miniTestData,
			}

			console.log('💾 Сохранение практического задания:', practiceData)
			this.isSaved = true
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-practice', practiceData)
				alert('Нет интернета. Сохранение практики поставлено в очередь.')
				setTimeout(() => {
					this.isSaving = false
				}, 300)
				return
			}
			this.$emit('save-practice', practiceData)
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},

		handleSaveDraft() {
			if (this.isSaving) return
			this.isSaving = true
			// Проверка мини-теста: предупреждение если не выбран правильный ответ
			if (this.includeMiniTest && this.miniTestQuestions.length > 0) {
				for (let i = 0; i < this.miniTestQuestions.length; i++) {
					const question = this.miniTestQuestions[i]
					if (question.type === 'single' || question.type === 'multiple') {
						const hasCorrectAnswer =
							question.answers && question.answers.some(a => a.isCorrect)
						if (!hasCorrectAnswer) {
							const confirmed = window.confirm(
								`⚠️ Внимание!\n\nВ вопросе ${
									i + 1
								} мини-теста не выбран правильный ответ.\n\nПродолжить сохранение черновика без указания правильного ответа?`
							)
							if (!confirmed) {
								question.isExpanded = true
								this.isSaving = false
								return
							}
							break // Спрашиваем только один раз для черновика
						}
					} else if (
						question.type === 'text' &&
						!question.correctText?.trim()
					) {
						const confirmed = window.confirm(
							`⚠️ Внимание!\n\nВ вопросе ${
								i + 1
							} мини-теста не указан правильный ответ.\n\nПродолжить сохранение черновика без указания правильного ответа?`
						)
						if (!confirmed) {
							question.isExpanded = true
							this.isSaving = false
							return
						}
						break
					}
				}
			}

			// Формируем miniTest с разворачиванием Proxy
			const miniTestData =
				this.includeMiniTest && this.miniTestQuestions.length > 0
					? {
							questions: [...this.miniTestQuestions].map(q => ({
								text: q.text,
								type: q.type,
								answers: q.answers
									? [...q.answers].map(a => ({
											text: a.text,
											isCorrect: a.isCorrect,
									  }))
									: [],
								correctText: q.correctText || '',
								explanation: q.explanation || '', // Пояснение к ответу
							})),
					  }
					: null

			const draftData = {
				...(this.editingPracticeId && { id: this.editingPracticeId }),
				title: this.practiceTitle.trim() || 'Черновик',
				module: this.selectedModule || this.currentModuleId,
				content: this.normalizeRichText(this.practiceContent),
				questions: [...this.questions].filter(q => q && q.trim()),
				materials: [...this.materials],
				deletedMaterials: [...this.deletedMaterials],
				includeMiniTest: this.includeMiniTest,
				miniTest: miniTestData,
				isDraft: true,
				is_draft: true,
			}

			console.log('📝 Сохранение черновика:', draftData)

			this.isSaved = true
			this.clearFormData()
			if (typeof navigator !== 'undefined' && !navigator.onLine) {
				this.enqueueSubmit('save-draft', draftData)
				alert('Нет интернета. Сохранение черновика поставлено в очередь.')
				setTimeout(() => {
					this.isSaving = false
				}, 300)
				return
			}
			this.$emit('save-draft', draftData)
			setTimeout(() => {
				this.isSaving = false
			}, 300)
		},

		handlePreview() {
			if (
				!this.practiceTitle &&
				!this.hasMeaningfulText(this.practiceContent)
			) {
				this.submitError =
					'Заполните хотя бы заголовок или содержание, чтобы открыть предпросмотр.'
				return
			}
			this.showPreviewModal = true
		},
		sanitizePreviewHtml(html = '') {
			return DOMPurify.sanitize(html || '', {
				ALLOWED_TAGS: [
					'p', 'br', 'strong', 'b', 'em', 'i', 'u', 's', 'del', 'ins',
					'a', 'ul', 'ol', 'li', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6',
					'blockquote', 'code', 'pre', 'hr', 'figure', 'figcaption', 'img',
					'table', 'caption', 'thead', 'tbody', 'tfoot', 'tr', 'th', 'td',
					'colgroup', 'col', 'div', 'span', 'label', 'input', 'small',
					'mark', 'sub', 'sup', 'oembed',
				],
				ALLOWED_ATTR: [
					'href', 'target', 'rel', 'src', 'srcset', 'sizes', 'alt', 'title',
					'class', 'id', 'style', 'width', 'height', 'colspan', 'rowspan',
					'type', 'checked', 'disabled', 'value', 'name', 'url', 'start',
					'reversed', 'dir', 'lang', 'aria-label',
				],
				ALLOW_DATA_ATTR: true,
				FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed'],
				FORBID_ATTR: ['on*'],
			})
		},
		getPlainTextFromHtml(html = '') {
			if (!html) return ''
			const container = document.createElement('div')
			container.innerHTML = html
			return (container.textContent || '').replace(/\u00A0/g, ' ').trim()
		},
		hasMeaningfulText(html = '') {
			const raw = String(html || '')
			if (!raw.trim()) return false
			if (/<(img|table|ul|ol|blockquote|pre|hr|input|oembed|figure)\b/i.test(raw)) {
				return true
			}
			return this.getPlainTextFromHtml(raw).length > 0
		},
		normalizeRichText(html = '') {
			const raw = String(html || '')
			return this.hasMeaningfulText(raw) ? raw : ''
		},
		openPracticePreviewInNewTab() {
			if (!this.canOpenPracticePreview) {
				alert('Сначала сохраните модуль и практику, затем откройте предпросмотр.')
				return
			}

			const moduleId = this.currentModuleId || this.selectedModule
			openInternalRouteInContext(this.$router, {
				name: 'TeacherScreen8Preview',
				query: {
					moduleId: String(moduleId),
					previewType: 'module',
					section: 'practice',
					practiceId: String(this.editingPracticeId),
					testMode: '1',
				},
			}, {
				title: 'Предпросмотр практики',
				leftBoundary: 48,
			})
		},

		handleClosePreview() {
			this.showPreviewModal = false
		},

    handleClose() {
      const hasRealChanges = this.hasUnsavedChanges()

      if (hasRealChanges) {
        const confirmed = window.confirm(
          'У вас есть несохраненные изменения. Вы уверены, что хотите закрыть форму?'
        )
        if (!confirmed) return
      }

      // Сохраняем ID перед очисткой
      const scrollToId = this.editingPracticeId
      
      this.clearFormData()
      
      // 🔥 Передаём ID для скролла
      this.$emit('cancel', { scrollToId, type: 'practice' })
  },
	},
}
</script>

<style scoped>
.form-offline-banner {
	margin-bottom: 12px;
	padding: 10px 12px;
	border: 1px solid #f59e0b;
	border-radius: 8px;
	background: #fffbeb;
	color: #92400e;
	font-size: 13px;
	font-weight: 500;
}

.practice-actions__button--disabled {
  opacity: 0.5;
  cursor: not-allowed;
  background-color: #9CA3AF !important;
  border: none !important;
  box-shadow: none !important;
}
.practice-actions__button--primary:not(.practice-actions__button--disabled):hover {
  opacity: 0.9;
  transform: translateY(-1px);
  transition: all 0.2s;
}




/* CSS переменные для единообразия */
:root {
	--primary-color: #0095ec;
	--primary-light: #e6f3ff;
	--border-color: #d1d5db;
	--border-light: #e5e7eb;
	--text-primary: #111827;
	--text-secondary: #4b5563;
	--text-muted: #6b7280;
	--text-placeholder: #9ca3af;
	--background-light: #f3f4f6;
	--border-radius: 6px;
	--transition: all 0.15s ease;
}

.practice-settings__close-icon {
	width: 18px;
	height: 18px;
	filter: brightness(0) invert(1);
}

.practice-creation-form {
	font-family: system-ui, -apple-system, BlinkMacSystemFont, 'Segoe UI',
		sans-serif;
	min-height: 100vh;
	color: #1f2933;
	box-sizing: border-box;
}

/* основной контейнер формы */
.practice-creation-form__container {
	position: relative;
	max-width: 980px;
	margin: 0 auto;
	background: #ffffff;
	border-radius: 12px;
	border: 1px solid #e5e7eb;
	padding: 24px 32px 0;
	box-sizing: border-box;
}

.practice-creation-form__background {
	display: none;
}

/* Настройки */
.practice-settings {
	padding: 0;
	border-bottom: 1px solid #e5e7eb;
	margin-bottom: 24px;
}

.practice-content {
	padding: 0;
}

/* Шапка настроек */
.practice-settings__header {
	display: flex;
	align-items: center;
	justify-content: space-between;
	margin-bottom: 16px;
	margin-left: -32px;
	margin-top: -24px;
	width: calc(100% + 64px);
	background: #03631d99;
	padding: 16px 20px;
	border-radius: 12px 12px 0 0;
	position: relative;
}

.practice-settings__header-background {
	display: none;
}

.practice-settings__header-title {
	font-size: 18px;
	font-weight: 500;
	color: #ffffff;
	margin: 0;
}

.practice-settings__close-button {
	width: 24px;
	height: 24px;
	border: none;
	background: transparent;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	cursor: pointer;
	transition: opacity 0.15s ease;
	color: #ffffff;
}

.practice-settings__close-button:hover {
	opacity: 0.8;
}

/* Поля формы */
.practice-settings__field {
	margin-bottom: 16px;
	display: flex;
	flex-direction: column;
	gap: 6px;
}

.practice-settings__label {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 8px 0;
}

.practice-required-mark {
	color: #e11d48;
	margin-left: 4px;
}

.practice-settings__input {
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 10px 12px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.practice-settings__input::placeholder {
	color: #9ca3af;
}

.practice-settings__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.practice-settings__input--error {
	border-color: #e11d48;
	box-shadow: 0 0 0 2px rgba(225, 29, 72, 0.12);
}

.practice-settings__error {
	font-size: 12px;
	color: #e11d48;
	margin: 0;
}

.practice-settings__autosave {
	margin-top: -6px;
	margin-bottom: 12px;
}

.practice-settings__autosave-text {
	font-size: 12px;
	color: #9ca3af;
}

/* Блок типа/опции */
.practice-settings__type-section {
	margin-top: 16px;
	margin-bottom: 18px;
}

.practice-settings__section-title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 12px 0;
}

.practice-settings__type-buttons {
	display: flex;
	gap: 12px;
}

.practice-type-btn {
	flex: 1;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	background: #ffffff;
	padding: 12px 14px;
	cursor: pointer;
	transition: all 0.15s ease;
	text-align: left;
	max-width: 550px;
}

.practice-type-btn:hover {
	border-color: #0095ec;
}

.practice-type-btn__heading {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
}

.practice-type-btn__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	width: 32px;
	height: 32px;
	border-radius: 4px;
}

.practice-type-btn__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 16px;
	font-weight: 600;
	letter-spacing: 0;
	line-height: normal;
}

.practice-type-btn__description {
	font-size: 13px;
	color: #6b7280;
	margin-left: 42px;
}

.practice-type-btn--active {
	border-color: #0095ec;
	background: #e6f3ff;
	box-shadow: 0 0 0 1px rgba(0, 149, 236, 0.3);
}

/* Заголовок содержания */
.practice-content__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 17px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 16px 0;
}

/* Секции */
.practice-section {
	margin-bottom: 24px;
}

.practice-section__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 12px;
}

.practice-section__title {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 12px 0;
}

.practice-section__limit {
	font-size: 12px;
	color: #9ca3af;
}

.practice-section__card {
	border-radius: 6px;
	border: 1px solid #e5e7eb;
	background: #ffffff;
	padding: 14px 16px;
}

.practice-section--content .practice-section__card {
	border: none;
	background: transparent;
	padding: 0;
}

.practice-section__card--mini-test {
	border-color: #ffc107;
	background: #fffbeb;
}

/* Поля содержания */
.practice-content__field {
	display: flex;
	flex-direction: column;
	gap: 6px;
	margin-bottom: 8px;
}

.practice-content__label {
	color: #4b5563;
	font-family: 'Poppins', Helvetica, sans-serif;
	font-size: 15px;
	font-weight: 500;
	letter-spacing: 0;
	line-height: normal;
	margin: 0 0 8px 0;
}

.practice-content__textarea {
	width: 100%;
	min-height: 80px;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 10px 12px;
	font-size: 14px;
	line-height: 1.5;
	resize: vertical;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
	font-family: inherit;
}

.practice-content__textarea::placeholder {
	color: #9ca3af;
}

.practice-content__textarea:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.practice-content__error {
	font-size: 12px;
	color: #e11d48;
	margin-top: 4px;
}

.practice-content__file-input {
	display: none;
}

/* Вопросы */
.practice-questions {
	display: flex;
	flex-direction: column;
	gap: 10px;
}

.practice-question {
	display: flex;
	align-items: center;
	gap: 10px;
}

.practice-question__number {
	font-size: 14px;
	font-weight: 600;
	color: #6b7280;
	min-width: 24px;
}

.practice-question__input {
	flex: 1;
	border-radius: 6px;
	border: 1px solid #d1d5db;
	padding: 10px 12px;
	font-size: 14px;
	outline: none;
	transition: border-color 0.15s ease, box-shadow 0.15s ease;
}

.practice-question__input::placeholder {
	color: #9ca3af;
}

.practice-question__input:focus {
	border-color: #0095ec;
	box-shadow: 0 0 0 2px rgba(0, 149, 236, 0.15);
}

.practice-question__remove {
	width: 28px;
	height: 28px;
	border-radius: 50%;
	border: none;
	background: #f3f4f6;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 14px;
	color: #6b7280;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
	flex-shrink: 0;
}

.practice-question__remove:hover {
	background: #fee2e2;
	color: #b91c1c;
}

.practice-content__add-btn {
	display: inline-flex;
	align-items: center;
	gap: 6px;
	margin-top: 12px;
	padding: 10px 16px;
	background: transparent;
	color: #0095ec;
	border: 1px dashed #0095ec;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
}

.practice-content__add-btn:hover {
	background: rgba(0, 149, 236, 0.05);
}

/* Загрузка материалов */
.practice-materials-upload {
	display: inline-block;
}

.practice-materials-upload__label {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	border-radius: 8px;
	background: #0095ec;
	color: #ffffff;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 18px;
	cursor: pointer;
	border: none;
	transition: background 0.15s ease, box-shadow 0.15s ease;
}

.practice-materials-upload__label:hover {
	background: #0080cb;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.35);
}

.practice-materials-upload__icon {
	display: inline-flex;
	align-items: center;
	justify-content: center;
}

.practice-materials-upload__hint {
	margin-left: 16px;
	display: inline-block;
}

.practice-materials-upload__description {
	font-size: 13px;
	color: #6b7280;
}

.practice-materials-upload__size-hint {
	font-size: 12px;
	color: #9ca3af;
}

/* Секция материалов */
.practice-section--materials .practice-section__card {
	background: #eaf5ff;
	border-color: #cfe3ff;
}

.practice-section--materials .practice-content__field {
	flex-direction: row;
	align-items: center;
	gap: 16px;
	margin-bottom: 0;
	flex-wrap: wrap;
}

/* Список материалов */
.practice-materials-list {
	margin-top: 14px;
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.practice-material-item {
	display: flex;
	align-items: center;
	justify-content: space-between;
	padding: 10px 12px;
	border-radius: 6px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	transition: border-color 0.15s ease;
}

.practice-material-item:hover {
	border-color: #d1d5db;
}

.practice-material-item__info {
	display: flex;
	align-items: center;
	gap: 10px;
	min-width: 0;
	flex: 1;
}

.practice-material-item__name {
	font-size: 13px;
	font-weight: 500;
	color: #111827;
	max-width: 300px;
	white-space: nowrap;
	overflow: hidden;
	text-overflow: ellipsis;
}

.practice-material-item__size {
	font-size: 12px;
	color: #6b7280;
	flex-shrink: 0;
}

.practice-material-item__badge {
	font-size: 10px;
	text-transform: uppercase;
	color: #059669;
	background: #ecfdf3;
	border-radius: 999px;
	padding: 2px 8px;
	font-weight: 500;
	flex-shrink: 0;
}

.practice-material-item__remove {
	width: 24px;
	height: 24px;
	border-radius: 50%;
	border: none;
	background: #f3f4f6;
	display: inline-flex;
	align-items: center;
	justify-content: center;
	font-size: 12px;
	color: #6b7280;
	cursor: pointer;
	transition: background 0.15s ease, color 0.15s ease;
	flex-shrink: 0;
	margin-left: 12px;
}

.practice-material-item__remove:hover {
	background: #fee2e2;
	color: #b91c1c;
}

/* Мини-тест */
.practice-mini-test__add-button {
	display: inline-flex;
	align-items: center;
	gap: 8px;
	padding: 10px 18px;
	background: #ffc107;
	color: #000000;
	border: none;
	border-radius: 6px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
	margin-bottom: 14px;
}

.practice-mini-test__add-button:hover {
	background: #ffb300;
}

.practice-mini-test__empty {
	text-align: center;
	padding: 32px 20px;
	background: #ffffff;
	border-radius: 6px;
	border: 2px dashed #e5e7eb;
}

.practice-mini-test__empty-text {
	font-size: 14px;
	color: #9ca3af;
	margin: 0;
}

.practice-mini-test__list {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

/* Вопрос мини-теста */
.mini-test-question {
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 8px;
	overflow: hidden;
}

.mini-test-question__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 12px 14px;
	background: #f9fafb;
	border-bottom: 1px solid #e5e7eb;
}

.mini-test-question__number {
	font-size: 14px;
	font-weight: 600;
	color: #111827;
}

.mini-test-question__actions {
	display: flex;
	gap: 8px;
}

.mini-test-question__toggle,
.mini-test-question__remove {
	width: 28px;
	height: 28px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 16px;
	font-weight: 600;
	cursor: pointer;
	transition: all 0.15s ease;
}

.mini-test-question__toggle {
	color: #ffc107;
}

.mini-test-question__toggle:hover {
	background: #fffbeb;
	border-color: #ffc107;
}

.mini-test-question__remove {
	color: #ef4444;
}

.mini-test-question__remove:hover {
	background: #fef2f2;
	border-color: #ef4444;
}

.mini-test-question__content {
	padding: 14px;
}

.mini-test-question__field {
	margin-bottom: 14px;
}

.mini-test-question__field:last-child {
	margin-bottom: 0;
}

/* Стили для поля пояснения */
.mini-test-question__field--explanation {
	margin-top: 16px;
	padding-top: 16px;
	border-top: 1px dashed #e5e7eb;
}

.mini-test-question__label-icon {
	margin-right: 6px;
}

.mini-test-question__label-hint {
	font-weight: 400;
	color: #9ca3af;
	font-size: 12px;
	margin-left: 4px;
}

.mini-test-question__textarea--explanation {
	background: linear-gradient(135deg, #fffbeb 0%, #fef3c7 100%);
	border-color: #fcd34d;
	min-height: 80px;
}

.mini-test-question__textarea--explanation:focus {
	border-color: #f59e0b;
	box-shadow: 0 0 0 2px rgba(245, 158, 11, 0.15);
}

.mini-test-question__textarea--explanation::placeholder {
	color: #92400e;
	opacity: 0.6;
}

.mini-test-question__label {
	display: block;
	font-size: 13px;
	font-weight: 500;
	color: #4b5563;
	margin-bottom: 8px;
}

.mini-test-question__textarea,
.mini-test-question__input,
.mini-test-question__select {
	width: 100%;
	padding: 10px 12px;
	background: #ffffff;
	border: 1px solid #d1d5db;
	border-radius: 6px;
	font-size: 14px;
	color: #111827;
	outline: none;
	transition: border-color 0.15s ease;
	font-family: inherit;
}

.mini-test-question__textarea {
	resize: vertical;
	min-height: 60px;
}

.mini-test-question__textarea:focus,
.mini-test-question__input:focus,
.mini-test-question__select:focus {
	border-color: #ffc107;
	box-shadow: 0 0 0 2px rgba(255, 193, 7, 0.15);
}

.mini-test-question__select {
	cursor: pointer;
}

.mini-test-question__answers {
	margin-top: 10px;
}

.mini-test-question__answers-header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	margin-bottom: 10px;
}

.mini-test-question__add-answer {
	background: transparent;
	color: #ffc107;
	border: 1px solid #ffc107;
	border-radius: 4px;
	padding: 6px 12px;
	font-size: 12px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
}

.mini-test-question__add-answer:hover {
	background: #fffbeb;
}

/* Варианты ответов */
.mini-test-answer {
	display: flex;
	align-items: center;
	gap: 10px;
	margin-bottom: 8px;
	padding: 10px 12px;
	background: #f9fafb;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
}

.mini-test-answer__radio,
.mini-test-answer__checkbox {
	width: 18px;
	height: 18px;
	cursor: pointer;
	accent-color: #ffc107;
	flex-shrink: 0;
}

.mini-test-answer__input {
	flex: 1;
	padding: 8px 10px;
	background: #ffffff;
	border: 1px solid #d1d5db;
	border-radius: 4px;
	font-size: 13px;
	color: #111827;
	outline: none;
	transition: border-color 0.15s ease;
}

.mini-test-answer__input:focus {
	border-color: #ffc107;
}

.mini-test-answer__remove {
	width: 24px;
	height: 24px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 4px;
	color: #ef4444;
	font-size: 14px;
	font-weight: 600;
	cursor: pointer;
	flex-shrink: 0;
	transition: all 0.15s ease;
}

.mini-test-answer__remove:hover:not(:disabled) {
	background: #fef2f2;
	border-color: #ef4444;
}

.mini-test-answer__remove:disabled {
	opacity: 0.3;
	cursor: not-allowed;
}

/* Кнопки действий */
.practice-actions {
	margin-top: 28px;
	margin-left: -32px;
	margin-right: -32px;
	padding: 16px 32px;
	background: #f5f6f8;
	border-top: 1px solid #e5e7eb;
	display: flex;
	align-items: center;
	gap: 12px;
	border-radius: 0 0 12px 12px;
}

.practice-actions__primary-group {
	display: flex;
	flex-direction: column;
	align-items: flex-end;
	gap: 6px;
}

.practice-actions__error {
	margin: 0;
	font-size: 12px;
	line-height: 1.4;
	color: #e11d48;
	text-align: right;
}

.practice-actions__button {
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	padding: 10px 20px;
	border: 1px solid transparent;
	cursor: pointer;
	white-space: nowrap;
	transition: background 0.15s ease, border-color 0.15s ease,
		box-shadow 0.15s ease;
}

.practice-actions__button--ghost {
	background: transparent;
	color: #4b5563;
	border-color: transparent;
	margin-right: auto;
}

.practice-actions__preview-tooltip {
	display: inline-flex;
	margin-right: auto;
}

.practice-actions__button--ghost:hover {
	background: #e5e7eb;
}

.practice-actions__button--secondary {
	background: #ffffff;
	color: #111827;
	border-color: #d1d5db;
}

.practice-actions__button--secondary:hover {
	background: #f9fafb;
	box-shadow: 0 4px 10px rgba(148, 163, 184, 0.35);
}

.practice-actions__button--primary {
	background: #0095ec;
	color: #ffffff;
	border-color: #0095ec;
	box-shadow: 0 4px 12px rgba(0, 149, 236, 0.35);
}

.practice-actions__button--primary:hover {
	background: #0080cb;
}

/* Модальное окно предпросмотра */
.preview-modal {
	position: fixed;
	top: 0;
	left: 0;
	width: 100%;
	height: 100%;
	background: rgba(0, 0, 0, 0.5);
	display: flex;
	align-items: center;
	justify-content: center;
	z-index: 1000;
	padding: 20px;
}

.preview-modal__content {
	background: #ffffff;
	border-radius: 12px;
	max-width: 700px;
	width: 100%;
	max-height: 90vh;
	display: flex;
	flex-direction: column;
	box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.preview-modal__header {
	display: flex;
	justify-content: space-between;
	align-items: center;
	padding: 18px 24px;
	border-bottom: 1px solid #e5e7eb;
}

.preview-modal__title {
	font-size: 18px;
	font-weight: 600;
	color: #111827;
	margin: 0;
}

.preview-modal__close {
	width: 32px;
	height: 32px;
	display: flex;
	align-items: center;
	justify-content: center;
	background: transparent;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 18px;
	color: #6b7280;
	cursor: pointer;
	transition: all 0.15s ease;
}

.preview-modal__close:hover {
	background: #fee2e2;
	border-color: #ef4444;
	color: #ef4444;
}

.preview-modal__body {
	flex: 1;
	overflow-y: auto;
	padding: 24px;
}

.preview-section {
	margin-bottom: 24px;
}

.preview-section:last-child {
	margin-bottom: 0;
}

.preview-section__title {
	font-size: 13px;
	font-weight: 600;
	color: #4b5563;
	margin: 0 0 10px 0;
	text-transform: uppercase;
	letter-spacing: 0.5px;
}

.preview-section__text {
	font-size: 15px;
	line-height: 1.6;
	color: #111827;
	margin: 0;
}

.preview-section__list {
	margin: 0;
	padding-left: 24px;
}

.preview-section__list li {
	font-size: 15px;
	line-height: 1.6;
	color: #111827;
	margin-bottom: 6px;
}

.preview-materials {
	list-style: none;
	margin: 0;
	padding: 0;
}

.preview-materials__item {
	display: flex;
	align-items: center;
	gap: 12px;
	padding: 10px 14px;
	background: #f9fafb;
	border-radius: 6px;
	margin-bottom: 8px;
}

.preview-materials__name {
	font-size: 14px;
	font-weight: 500;
	color: #111827;
}

.preview-materials__size {
	font-size: 12px;
	color: #6b7280;
}

.preview-test {
	display: flex;
	flex-direction: column;
	gap: 14px;
}

.preview-test-question {
	padding: 14px;
	background: #f9fafb;
	border-radius: 8px;
	border: 1px solid #e5e7eb;
}

.preview-test-question__number {
	font-size: 12px;
	font-weight: 600;
	color: #0095ec;
	margin-bottom: 8px;
}

.preview-test-question__text {
	font-size: 15px;
	font-weight: 500;
	color: #111827;
	margin-bottom: 12px;
}

.preview-test-answers {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.preview-test-answer {
	display: flex;
	align-items: center;
	gap: 10px;
	padding: 8px 12px;
	background: #ffffff;
	border-radius: 6px;
}

.preview-test-answer__input {
	width: 16px;
	height: 16px;
	cursor: not-allowed;
}

.preview-test-answer__text {
	font-size: 14px;
	color: #4b5563;
}

.preview-test-answer--correct {
	background: #ecfdf5;
	border: 1px solid #10b981;
}

.preview-test-answer--correct .preview-test-answer__text {
	color: #065f46;
	font-weight: 500;
}

.preview-test-answer__badge {
	font-size: 11px;
	font-weight: 600;
	color: #059669;
	background: #d1fae5;
	padding: 2px 8px;
	border-radius: 999px;
	margin-left: auto;
}

.preview-test-text-answer__input {
	width: 100%;
	padding: 10px 12px;
	background: #ffffff;
	border: 1px solid #e5e7eb;
	border-radius: 6px;
	font-size: 14px;
	color: #9ca3af;
	cursor: not-allowed;
}

.preview-modal__footer {
	padding: 18px 24px;
	border-top: 1px solid #e5e7eb;
	display: flex;
	justify-content: center;
}

.preview-modal__button {
	padding: 10px 28px;
	background: #0095ec;
	color: #ffffff;
	border: none;
	border-radius: 8px;
	font-size: 14px;
	font-weight: 500;
	cursor: pointer;
	transition: all 0.15s ease;
}

.preview-modal__button:hover {
	background: #0080cb;
}

/* Адаптив */
@media (max-width: 768px) {
	.practice-creation-form__container {
		margin: 0 12px;
		padding: 20px 16px 0;
	}

	.practice-settings__header {
		margin-left: -16px;
		width: calc(100% + 32px);
	}

	.practice-actions {
		margin-left: -16px;
		margin-right: -16px;
		padding: 14px 16px;
		flex-wrap: wrap;
	}

	.practice-section--materials .practice-content__field {
		flex-direction: column;
		align-items: flex-start;
	}

	.practice-materials-upload__hint {
		margin-left: 0;
		margin-top: 10px;
	}
}
</style>
